/**
 * PII scrubbing hooks for Sentry.
 *
 * Plug into `Sentry.init({ beforeSend, beforeBreadcrumb })` on both the browser
 * and server SDKs. Every field is stripped defensively — the SDK's own scrubbers
 * only cover a subset and we can't assume any Sentry server-side data-scrubbing
 * rule is configured.
 *
 * What we remove:
 *   - `Cookie`, `Set-Cookie`, and `Authorization` headers (any casing)
 *   - Any header whose name contains `token`, `apikey`, `api-key`, `secret`,
 *     `session`, `csrf`, `x-supabase-*`, `x-lovable-*`
 *   - `document.cookie` references in breadcrumb data / extras
 *   - Common auth-shaped query params: `access_token`, `refresh_token`, `token`,
 *     `code`, `id_token`, `state`, `session`
 *   - JWT-shaped strings (three dot-separated base64url segments) inside strings
 *   - Request/response bodies on network breadcrumbs (kept: method, status, url)
 *   - `event.user.email` and `event.user.ip_address` (keep only opaque `id`)
 */

type AnyRecord = Record<string, unknown>;

const SENSITIVE_HEADER_EXACT = new Set([
  "cookie",
  "set-cookie",
  "authorization",
  "proxy-authorization",
  "x-api-key",
  "x-auth-token",
  "x-supabase-auth",
]);

const SENSITIVE_HEADER_SUBSTRING = [
  "token",
  "apikey",
  "api-key",
  "secret",
  "session",
  "csrf",
  "x-supabase-",
  "x-lovable-",
];

const SENSITIVE_QUERY_KEYS = new Set([
  "access_token",
  "refresh_token",
  "token",
  "id_token",
  "code",
  "state",
  "session",
  "apikey",
  "api_key",
  "authorization",
  "password",
]);

const REDACTED = "[Filtered]";
// Three base64url segments separated by dots — matches Supabase / OAuth JWTs.
const JWT_RE = /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;

function isSensitiveHeader(name: string): boolean {
  const lower = name.toLowerCase();
  if (SENSITIVE_HEADER_EXACT.has(lower)) return true;
  return SENSITIVE_HEADER_SUBSTRING.some((needle) => lower.includes(needle));
}

/** Redact JWT-shaped tokens inside any string. */
function scrubString(value: string): string {
  if (typeof value !== "string") return value;
  return value.replace(JWT_RE, REDACTED);
}

/** Remove sensitive headers from any object keyed by header name. */
function scrubHeaders(headers: AnyRecord | undefined | null): AnyRecord | undefined {
  if (!headers || typeof headers !== "object") return headers ?? undefined;
  const out: AnyRecord = {};
  for (const [name, value] of Object.entries(headers)) {
    out[name] = isSensitiveHeader(name) ? REDACTED : value;
  }
  return out;
}

/** Redact auth-shaped query params in a URL string, and any JWTs in it. */
function scrubUrl(url: string): string {
  try {
    const u = new URL(url, "http://placeholder.local");
    let touched = false;
    u.searchParams.forEach((_v, k) => {
      if (SENSITIVE_QUERY_KEYS.has(k.toLowerCase())) {
        u.searchParams.set(k, REDACTED);
        touched = true;
      }
    });
    const rebuilt = touched
      ? url.startsWith("http")
        ? u.toString()
        : `${u.pathname}${u.search}${u.hash}`
      : url;
    return scrubString(rebuilt);
  } catch {
    return scrubString(url);
  }
}

/** Walk a value recursively and scrub headers/URLs/JWTs. Bounded depth. */
function deepScrub(input: unknown, depth = 0): unknown {
  if (depth > 6 || input == null) return input;
  if (typeof input === "string") return scrubString(input);
  if (Array.isArray(input)) return input.map((v) => deepScrub(v, depth + 1));
  if (typeof input !== "object") return input;

  const obj = input as AnyRecord;
  const out: AnyRecord = {};
  for (const [key, value] of Object.entries(obj)) {
    const lower = key.toLowerCase();
    if (lower === "headers") {
      out[key] = scrubHeaders(value as AnyRecord);
      continue;
    }
    if (lower === "cookies" || lower === "cookie" || lower === "set-cookie") {
      out[key] = REDACTED;
      continue;
    }
    if (lower === "url" && typeof value === "string") {
      out[key] = scrubUrl(value);
      continue;
    }
    // Never forward request/response bodies from network breadcrumbs — they
    // regularly contain form data, tokens, or PII.
    if (lower === "body" || lower === "data" || lower === "response") {
      out[key] = REDACTED;
      continue;
    }
    if (SENSITIVE_QUERY_KEYS.has(lower) || isSensitiveHeader(key)) {
      out[key] = REDACTED;
      continue;
    }
    out[key] = deepScrub(value, depth + 1);
  }
  return out;
}

/** Minimal shape we touch — kept local so this module has no Sentry dep. */
type SentryLikeEvent = {
  request?: {
    headers?: AnyRecord;
    cookies?: unknown;
    query_string?: string | AnyRecord;
    url?: string;
    data?: unknown;
  };
  contexts?: AnyRecord;
  extra?: AnyRecord;
  tags?: AnyRecord;
  user?: { id?: string; email?: string; ip_address?: string; username?: string };
  breadcrumbs?: Array<SentryLikeBreadcrumb>;
  message?: string;
  exception?: { values?: Array<{ value?: string }> };
};

type SentryLikeBreadcrumb = {
  category?: string;
  message?: string;
  data?: AnyRecord;
};

/** `beforeSend` hook — pass into `Sentry.init({ beforeSend })`. */
export function scrubSentryEvent<T extends SentryLikeEvent>(event: T): T {
  if (event.request) {
    event.request.headers = scrubHeaders(event.request.headers) as AnyRecord;
    if (event.request.cookies) event.request.cookies = REDACTED;
    if (typeof event.request.url === "string") {
      event.request.url = scrubUrl(event.request.url);
    }
    if (event.request.query_string) {
      event.request.query_string =
        typeof event.request.query_string === "string"
          ? scrubUrl(`?${event.request.query_string}`).replace(/^\?/, "")
          : (deepScrub(event.request.query_string) as AnyRecord);
    }
    // Drop any request body outright.
    if (event.request.data != null) event.request.data = REDACTED;
  }

  if (event.contexts) event.contexts = deepScrub(event.contexts) as AnyRecord;
  if (event.extra) event.extra = deepScrub(event.extra) as AnyRecord;
  if (event.tags) event.tags = deepScrub(event.tags) as AnyRecord;

  // Keep an opaque user id for grouping; strip email / IP / username.
  if (event.user) {
    event.user = { id: event.user.id };
  }

  if (typeof event.message === "string") event.message = scrubString(event.message);
  if (event.exception?.values) {
    for (const v of event.exception.values) {
      if (typeof v.value === "string") v.value = scrubString(v.value);
    }
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs
      .map((b) => scrubSentryBreadcrumb(b))
      .filter((b): b is SentryLikeBreadcrumb => b !== null);
  }
  return event;
}

/** `beforeBreadcrumb` hook — pass into `Sentry.init({ beforeBreadcrumb })`. */
export function scrubSentryBreadcrumb<T extends SentryLikeBreadcrumb>(
  crumb: T,
): T | null {
  // Drop entire breadcrumb categories that leak by nature.
  if (crumb.category === "console" && typeof crumb.message === "string") {
    crumb.message = scrubString(crumb.message);
  }
  if (crumb.data) crumb.data = deepScrub(crumb.data) as AnyRecord;
  if (typeof crumb.message === "string") crumb.message = scrubString(crumb.message);
  return crumb;
}

/** Convenience bundle to spread into `Sentry.init({ ... })`. */
export const sentryScrubbers = {
  beforeSend: scrubSentryEvent,
  beforeBreadcrumb: scrubSentryBreadcrumb,
  sendDefaultPii: false as const,
};
